var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useCallback, useRef } from "react";
import { reportsApi } from "@api/enpoints/reports-api";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
const extractReportColumnNodes = (accessPoints) => {
    var _a;
    const apsWithHorizontal = accessPoints.filter((ap) => { var _a, _b, _c, _d; return ((_b = (_a = ap.nodeColumns) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 && ((_d = (_c = ap.periodRows) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0; });
    const useApPrefix = apsWithHorizontal.length > 1;
    const seen = new Set();
    const out = [];
    for (const ap of accessPoints) {
        const cols = (_a = ap.nodeColumns) !== null && _a !== void 0 ? _a : [];
        for (const c of cols) {
            const uid = c.nodeUid;
            if (!uid || seen.has(uid))
                continue;
            seen.add(uid);
            out.push({
                value: uid,
                label: useApPrefix
                    ? `${ap.accessPointName} — ${c.displayName || uid}`
                    : c.displayName || uid,
            });
        }
    }
    return out;
};
/**
 * Transforms API response to table row format for display
 */
const transformToTableRows = (accessPoints) => {
    var _a, _b, _c;
    if (!accessPoints.length)
        return [];
    const usesHorizontal = accessPoints.some((ap) => { var _a, _b, _c, _d; return ((_b = (_a = ap.nodeColumns) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 && ((_d = (_c = ap.periodRows) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0; });
    if (usesHorizontal) {
        const periodMap = new Map();
        for (const ap of accessPoints) {
            const nodeUids = ((_a = ap.nodeColumns) !== null && _a !== void 0 ? _a : []).map((c) => c.nodeUid);
            for (const pr of (_b = ap.periodRows) !== null && _b !== void 0 ? _b : []) {
                let row = periodMap.get(pr.period);
                if (!row) {
                    row = {
                        period: pr.period,
                        periodStart: String(pr.periodStart),
                        periodEnd: String(pr.periodEnd),
                    };
                    periodMap.set(pr.period, row);
                }
                ((_c = pr.nodeValues) !== null && _c !== void 0 ? _c : []).forEach((m, idx) => {
                    var _a, _b, _c, _d;
                    const uid = nodeUids[idx];
                    if (!uid)
                        return;
                    const cell = {
                        minTemp: (_a = m.minTemperature) !== null && _a !== void 0 ? _a : null,
                        avgTemp: (_b = m.avgTemperature) !== null && _b !== void 0 ? _b : null,
                        maxTemp: (_c = m.maxTemperature) !== null && _c !== void 0 ? _c : null,
                        readingCount: (_d = m.readingCount) !== null && _d !== void 0 ? _d : 0,
                    };
                    row[uid] = cell;
                });
            }
        }
        return Array.from(periodMap.values()).sort((a, b) => new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime());
    }
    const allPeriods = new Map();
    accessPoints.forEach((ap) => {
        ap.data.forEach((periodData) => {
            if (!allPeriods.has(periodData.period)) {
                allPeriods.set(periodData.period, {
                    periodStart: periodData.periodStart,
                    periodEnd: periodData.periodEnd,
                });
            }
        });
    });
    const rows = [];
    allPeriods.forEach((periodInfo, period) => {
        const row = {
            period,
            periodStart: periodInfo.periodStart,
            periodEnd: periodInfo.periodEnd,
        };
        accessPoints.forEach((ap) => {
            const apPeriodData = ap.data.find((d) => d.period === period);
            const apData = apPeriodData
                ? {
                    minTemp: apPeriodData.minTemperature,
                    avgTemp: apPeriodData.avgTemperature,
                    maxTemp: apPeriodData.maxTemperature,
                    readingCount: apPeriodData.readingCount,
                }
                : {
                    minTemp: null,
                    avgTemp: null,
                    maxTemp: null,
                    readingCount: 0,
                };
            row[ap.accessPointUid] = apData;
        });
        rows.push(row);
    });
    rows.sort((a, b) => new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime());
    return rows;
};
/**
 * Builds API date strings from the user's local calendar day without shifting the day
 * when converted to UTC (unlike `Date#toISOString()` on local midnight, which can fall on
 * the previous UTC date for zones ahead of UTC).
 */
// Preserve local date+time as UTC (avoids timezone-shift of the calendar day).
const reportRangeStartUtcIso = (d) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), 0, 0)).toISOString();
const reportRangeEndUtcIso = (d) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), 59, 999)).toISOString();
export const useReportsData = (params) => {
    const { selectedNodes, nodeStatus, fromDate, toDate, interval, showBy } = params;
    const { t } = useTranslation(["common"]);
    const [reportData, setReportData] = useState({
        data: [],
        loading: false,
        error: null,
    });
    const [temperatureData, setTemperatureData] = useState([]);
    const [reportColumnNodes, setReportColumnNodes] = useState([]);
    const isFetchingRef = useRef(false);
    const fetchData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedNodes.length) {
            setReportData({ data: [], loading: false, error: null });
            setTemperatureData([]);
            setReportColumnNodes([]);
            return;
        }
        // Prevent duplicate in-flight requests
        if (isFetchingRef.current)
            return;
        isFetchingRef.current = true;
        setReportData((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const response = yield reportsApi.getTemperatureReport({
                nodeUids: selectedNodes.map((n) => String(n.value)),
                nodeStatus: nodeStatus || "All",
                fromDate: reportRangeStartUtcIso(fromDate),
                toDate: reportRangeEndUtcIso(toDate),
                interval,
                showBy,
            });
            if (response.isSuccess && response.data) {
                setReportData({
                    data: response.data.accessPoints,
                    loading: false,
                    error: null,
                });
                const aps = response.data.accessPoints;
                setTemperatureData(transformToTableRows(aps));
                const fromApi = extractReportColumnNodes(aps);
                setReportColumnNodes(fromApi.length > 0 ? fromApi : []);
            }
            else {
                throw new Error(response.message || "Failed to fetch report data");
            }
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Unknown error";
            setReportData({
                data: [],
                loading: false,
                error: errorMessage,
            });
            setTemperatureData([]);
            setReportColumnNodes([]);
            console.error("Report fetch error:", err);
        }
        finally {
            isFetchingRef.current = false;
        }
    }), [selectedNodes, nodeStatus, fromDate, toDate, interval, showBy, t]);
    const downloadExcelReport = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedNodes.length) {
            toast.error(t("common:toasts.selectAtLeastOneNode"));
            return;
        }
        try {
            toast.info(t("common:toasts.generatingExcelReport"));
            const blob = yield reportsApi.exportTemperatureReport({
                nodeUids: selectedNodes.map((n) => String(n.value)),
                nodeStatus: nodeStatus || "All",
                fromDate: reportRangeStartUtcIso(fromDate),
                toDate: reportRangeEndUtcIso(toDate),
                interval,
                showBy,
            });
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `TemperatureReport_${new Date().toISOString().split("T")[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success(t("common:toasts.excelReportDownloaded"));
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : t("common:toasts.failedToDownloadExcelReport");
            toast.error(errorMessage);
            console.error("Download error:", err);
        }
    }), [selectedNodes, nodeStatus, fromDate, toDate, interval, showBy, t]);
    const downloadPdfReport = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedNodes.length) {
            toast.error(t("common:toasts.selectAtLeastOneNode"));
            return;
        }
        try {
            toast.info(t("common:toasts.generatingPdfReport"));
            const blob = yield reportsApi.exportTemperatureReportPdf({
                nodeUids: selectedNodes.map((n) => String(n.value)),
                nodeStatus: nodeStatus || "All",
                fromDate: reportRangeStartUtcIso(fromDate),
                toDate: reportRangeEndUtcIso(toDate),
                interval,
                showBy,
            });
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `TemperatureReport_${new Date().toISOString().split("T")[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success(t("common:toasts.pdfReportDownloaded"));
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : t("common:toasts.failedToDownloadPdfReport");
            toast.error(errorMessage);
            console.error("Download error:", err);
        }
    }), [selectedNodes, nodeStatus, fromDate, toDate, interval, showBy, t]);
    const clearData = useCallback(() => {
        setReportData({ data: [], loading: false, error: null });
        setTemperatureData([]);
        setReportColumnNodes([]);
    }, []);
    return {
        reportData,
        temperatureData,
        reportColumnNodes,
        loading: reportData.loading,
        error: reportData.error,
        downloadExcelReport,
        downloadPdfReport,
        refetchData: fetchData,
        clearData,
    };
};
